import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { articles, getArticle, formatCount } from "@/mocks/articles";
import { usePageSeo } from "@/hooks/usePageSeo";
import { ArticleCard } from "@/components/feature/ArticleCard";

export default function ArticlePage() {
  const { slug = "" } = useParams();
  const article = getArticle(slug);
  const [liked, setLiked] = useState(false);
  const [copied, setCopied] = useState(false);

  usePageSeo({
    title: article ? `${article.title} - 吃瓜现场` : "内容未找到",
    description: article ? article.excerpt : "没有找到对应的内容。",
    ld: article
      ? {
          "@context": "https://schema.org",
          "@type": "Article",
          headline: article.title,
          inLanguage: "zh-CN",
          description: article.excerpt,
          datePublished: `2026-${article.date}T${article.time}:00+08:00`,
          author: { "@type": "Organization", name: article.source },
          publisher: { "@type": "Organization", name: "吃瓜现场" },
          keywords: article.tags.join(","),
          articleSection: article.catName,
        }
      : undefined,
  });

  if (!article) {
    return (
      <div className="flex flex-col items-center justify-center px-4 py-28 text-center">
        <i className="ri-file-warning-line text-5xl text-background-400" aria-hidden="true"></i>
        <h1 className="mt-4 font-heading text-2xl font-black text-foreground-950">内容走丢了</h1>
        <p className="mt-2 text-sm text-foreground-600">这条瓜可能已被移动或删除，去别的频道看看吧。</p>
        <Link to="/" className="mt-6 rounded-full bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-600">
          返回首页
        </Link>
      </div>
    );
  }

  const related = articles
    .filter((a) => a.cat === article.cat && a.slug !== article.slug)
    .concat(articles.filter((a) => a.cat !== article.cat))
    .slice(0, 3);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    }
  };

  return (
    <article className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-10">
      {/* 面包屑 */}
      <nav className="flex flex-wrap items-center gap-1.5 text-sm text-foreground-600" aria-label="面包屑">
        <Link to="/" className="hover:text-primary-700">首页</Link>
        <i className="ri-arrow-right-s-line text-foreground-400" aria-hidden="true"></i>
        <Link to={`/category/${article.cat}`} className="hover:text-primary-700">{article.catName}</Link>
        <i className="ri-arrow-right-s-line text-foreground-400" aria-hidden="true"></i>
        <span className="line-clamp-1 text-foreground-950">{article.title}</span>
      </nav>

      {/* 正文主体 */}
      <div className="mt-6 grid grid-cols-1 gap-8 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <div className="rounded-2xl border border-background-200/70 bg-background-50 p-5 md:p-8">
            <div className="flex flex-wrap items-center gap-2">
              <Link
                to={`/category/${article.cat}`}
                className="inline-flex items-center gap-1.5 rounded-full bg-primary-100 px-3 py-1 text-xs font-medium text-primary-700 hover:bg-primary-200"
              >
                <i className="ri-price-tag-3-line" aria-hidden="true"></i> {article.catName}
              </Link>
              {article.tags.slice(0, 2).map((t) => (
                <span key={t} className="inline-flex items-center rounded-full bg-background-100 px-3 py-1 text-xs text-foreground-600">
                  #{t}
                </span>
              ))}
            </div>

            <h1 className="mt-4 font-heading text-2xl font-black leading-snug tracking-tight text-foreground-950 md:text-3xl">
              {article.title}
            </h1>
            <p className="mt-3 text-base leading-relaxed text-foreground-600">{article.excerpt}</p>

            <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-2 border-y border-background-200/70 py-3.5 text-xs text-foreground-600">
              <span className="inline-flex items-center gap-1.5">
                <span className="flex h-6 w-6 items-center justify-center rounded-full bg-accent-100 text-accent-700">
                  <i className="ri-user-3-line text-xs" aria-hidden="true"></i>
                </span>
                <span className="font-medium text-foreground-800">{article.source}</span>
              </span>
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-calendar-line text-primary-400" aria-hidden="true"></i> {article.date} {article.time}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-eye-line" aria-hidden="true"></i> {formatCount(article.views)} 围观
              </span>
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-chat-3-line" aria-hidden="true"></i> {formatCount(article.comments)} 讨论
              </span>
            </div>

            <img
              src={article.cover}
              alt={`${article.title}题图`}
              title={`${article.title} - 吃瓜现场`}
              className="mt-6 aspect-[16/9] w-full rounded-2xl object-cover"
            />

            <div className="prose-cg mt-6">
              {article.body.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>

            {/* 互动 */}
            <div className="mt-8 flex flex-wrap items-center gap-3 border-t border-background-200/70 pt-5">
              <button
                type="button"
                onClick={() => setLiked((v) => !v)}
                className={`inline-flex items-center gap-2 whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors cursor-pointer ${
                  liked ? "bg-primary-500 text-background-50" : "bg-background-100 text-foreground-800 hover:bg-primary-100 hover:text-primary-700"
                }`}
              >
                <i className={`${liked ? "ri-heart-fill" : "ri-heart-line"} text-base`} aria-hidden="true"></i>
                {liked ? "已喜欢" : "喜欢这篇"}
              </button>
              <button
                type="button"
                onClick={copyLink}
                className={`inline-flex items-center gap-2 whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors cursor-pointer ${
                  copied ? "bg-accent-600 text-background-50" : "bg-background-100 text-foreground-800 hover:bg-accent-100 hover:text-accent-800"
                }`}
              >
                <i className={`${copied ? "ri-check-line" : "ri-link"} text-base`} aria-hidden="true"></i>
                {copied ? "链接已复制" : "分享链接"}
              </button>
              <div className="ml-auto flex flex-wrap gap-2">
                {["ri-wechat-line", "ri-weibo-line", "ri-qq-line"].map((ic) => (
                  <span
                    key={ic}
                    className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full bg-background-100 text-foreground-700 transition-colors hover:bg-primary-500 hover:text-background-50"
                    aria-hidden="true"
                  >
                    <i className={`${ic} text-base`}></i>
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* 同频道入口 */}
          <Link
            to={`/category/${article.cat}`}
            className="mt-5 flex items-center justify-between rounded-2xl bg-background-100/60 px-5 py-4 transition-colors hover:bg-primary-100/60"
          >
            <span className="flex items-center gap-2 text-sm text-foreground-800">
              <i className="ri-apps-2-line text-primary-500" aria-hidden="true"></i>
              查看更多「{article.catName}」内容
            </span>
            <i className="ri-arrow-right-line text-primary-500" aria-hidden="true"></i>
          </Link>
        </div>

        {/* 侧栏 */}
        <aside className="space-y-6" aria-label="文章侧栏">
          <div className="rounded-2xl bg-foreground-950 p-5 text-background-50">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary-500">
              <i className="ri-flashlight-fill text-lg" aria-hidden="true"></i>
            </span>
            <h3 className="mt-4 font-heading text-lg font-bold">还想看更多瓜？</h3>
            <p className="mt-2 text-sm leading-relaxed text-foreground-200">
              全网热讯持续刷新，网页在线看、直接打开、免下载，随时随地跟上实时热点。
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              <Link to="/hot" className="inline-flex items-center gap-1.5 rounded-full bg-primary-500 px-4 py-2 text-sm font-medium hover:bg-primary-600">
                实时热榜
              </Link>
              <Link to="/" className="inline-flex items-center gap-1.5 rounded-full bg-background-50/15 px-4 py-2 text-sm hover:bg-background-50/25">
                首页
              </Link>
            </div>
          </div>

          <div className="rounded-2xl border border-background-200/70 bg-background-100/50 p-5">
            <h3 className="flex items-center gap-2 font-heading text-base font-black text-foreground-950">
              <i className="ri-star-line text-secondary-500" aria-hidden="true"></i> 作者 / 栏目
            </h3>
            <div className="mt-4 flex items-center gap-3">
              <span className="flex h-12 w-12 items-center justify-center rounded-full bg-accent-100 text-accent-700">
                <i className="ri-user-3-line text-xl" aria-hidden="true"></i>
              </span>
              <div>
                <p className="font-medium text-foreground-950">{article.source}</p>
                <p className="text-xs text-foreground-600">{article.catName}频道 · 每日更新</p>
              </div>
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              {article.tags.map((t) => (
                <Link key={t} to="/hot" className="inline-flex items-center rounded-full bg-background-50 px-3 py-1 text-xs text-foreground-700 hover:text-primary-700">
                  #{t}
                </Link>
              ))}
            </div>
          </div>
        </aside>
      </div>

      {/* 相关推荐 */}
      <section className="mt-12" aria-labelledby="rel-title">
        <h2 id="rel-title" className="flex items-center gap-2 font-heading text-2xl font-black tracking-tight text-foreground-950">
          <i className="ri-arrow-right-circle-line text-primary-500" aria-hidden="true"></i>
          相关推荐
        </h2>
        <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {related.map((a) => (
            <ArticleCard key={a.slug} article={a} />
          ))}
        </div>
      </section>
    </article>
  );
}